import { Button, DrawerCloseButton, Flex, Text } from '@chakra-ui/react';
import type { BigNumber } from '@ethersproject/bignumber';
import { parseUnits } from '@ethersproject/units';
import Balance from '@ui/components/Balance';
import Info from '@ui/components/Info';
import { InDesktop } from '@ui/components/MobileOrDesktop';
import {
  ModalOrDragDrawerContent,
  ModalOrDrawer,
  ModalOrDrawerBodyWithMask,
  ModalOrDrawerFooter,
  ModalOrDrawerHeader,
  ModalOrDrawerOverlay,
} from '@ui/components/ModalOrDrawer';
import { MonthlyRewardChart } from '@ui/components/MonthlyRewardChart';
import { useStakingTour } from '@ui/components/Tours';
import {
  StakeTransactionCompleted,
  TransactionError,
  TransactionPending,
} from '@ui/components/TransactionDrawer';
import type { CoinMode } from '@ui/hooks/useCoinMode';
import { COIN_MODE } from '@ui/hooks/useCoinMode';
import { useCoinUsdValue } from '@ui/hooks/useCoinUsdValue';
import useStakeCoin from '@ui/hooks/useStakeCoin';
import { useTranslations } from '@ui/i18n';
import { useVenoSDK } from '@ui/providers/VenoSDKProvider';
import { formatNumber } from '@ui/utils/format';
import type { EthersError } from '@veno-app/sdk';
import { useMemo, useState } from 'react';

import { doApprovals, getReceivedCoin } from './util';

export interface StakeCroDrawerProps {
  coinMode: CoinMode;
  isDrawerOpen: boolean;
  onCloseDrawer: () => void;
  amountToStake: string;
  apyList: number[];
  balance: number;
  balanceUsd: number;
  croPerStcro: number;
  account: string;
  oneYearUsdRewards: number | undefined;
}

enum StakeCroView {
  CONFIRM,
  PENDING,
  ERROR,
  COMPLETE,
}

const StakeCroDrawer = ({
  coinMode,
  isDrawerOpen,
  onCloseDrawer,
  amountToStake,
  apyList,
  balance,
  balanceUsd,
  croPerStcro,
  account,
  oneYearUsdRewards,
}: StakeCroDrawerProps) => {
  const t = useTranslations();
  const coinModeInfo = COIN_MODE[coinMode];
  const sdk = useVenoSDK();
  const { mutateAsync: stakeCoin } = useStakeCoin();
  const [error, setError] = useState<EthersError | null>(null);
  const [transactionPending, setTransactionPending] = useState(false);
  const [receivedLCoin, setReceivedLCoin] = useState<BigNumber | null>(null);
  const [nSteps, setNSteps] = useState(1);
  const [currentStep, setCurrentStep] = useState(1);
  const numberToStake = useMemo(
    () => parseFloat(amountToStake),
    [amountToStake],
  );
  const currentView = useMemo<StakeCroView>(() => {
    if (error) {
      return StakeCroView.ERROR;
    }
    if (transactionPending) {
      return StakeCroView.PENDING;
    }
    if (receivedLCoin) {
      return StakeCroView.COMPLETE;
    }
    return StakeCroView.CONFIRM;
  }, [error, receivedLCoin, transactionPending]);

  const doStake = async () => {
    useStakingTour.getState().next();

    try {
      setTransactionPending(true);
      setError(null);
      const amount = parseUnits(amountToStake, coinModeInfo.decimals);
      await doApprovals(
        sdk,
        account,
        amount,
        setNSteps,
        setCurrentStep,
        coinMode,
      );
      const p = await stakeCoin({
        receiver: account ?? '',
        amount: amount,
      });
      setTransactionPending(false);
      setReceivedLCoin(getReceivedCoin(p, coinMode, sdk));

      useStakingTour.getState().next();
    } catch (err) {
      console.error(err);
      setTransactionPending(false);
      setError(err as EthersError);
    }
  };

  const closeAndReset = () => {
    onCloseDrawer();
    setTransactionPending(false);
    setReceivedLCoin(null);
    setError(null);
    useStakingTour.getState().next();
  };
  return (
    <ModalOrDrawer
      isOpen={isDrawerOpen}
      onClose={closeAndReset}
      scrollBehavior="inside"
      placement="bottom"
      size={{
        base: 'full',
        desktop: 'md',
      }}
    >
      <ModalOrDrawerOverlay />
      <ModalOrDragDrawerContent>
        <ModalOrDrawerHeader>
          {t('Stake {coin}', {
            coin: coinModeInfo.name,
          })}
        </ModalOrDrawerHeader>
        <InDesktop>
          <DrawerCloseButton />
        </InDesktop>
        <ModalOrDrawerBodyWithMask>
          <StakeCroContent
            coinMode={coinMode}
            currentView={currentView}
            receivedLCoin={receivedLCoin}
            amountToStake={amountToStake}
            apyList={apyList}
            balance={balance}
            balanceUsd={balanceUsd}
            croPerStcro={croPerStcro}
            error={error}
            numberToStake={numberToStake}
            oneYearUsdRewards={oneYearUsdRewards}
            nSteps={nSteps}
            currentStep={currentStep}
          />
        </ModalOrDrawerBodyWithMask>
        <ModalOrDrawerFooter>
          <StakeCroFooter
            coinMode={coinMode}
            currentView={currentView}
            closeAndReset={closeAndReset}
            doStake={doStake}
            transactionPending={transactionPending}
          />
        </ModalOrDrawerFooter>
      </ModalOrDragDrawerContent>
    </ModalOrDrawer>
  );
};

type StakeCroContentProps = {
  coinMode: CoinMode;
  currentView: StakeCroView;
  receivedLCoin: BigNumber | null;
  amountToStake: string;
  apyList: number[];
  balance: number;
  balanceUsd: number;
  croPerStcro: number;
  error: EthersError | null;
  numberToStake: number;
  oneYearUsdRewards: number | undefined;
  nSteps?: number;
  currentStep?: number;
};
const StakeCroContent = ({
  coinMode,
  currentView,
  receivedLCoin,
  error,
  balance,
  balanceUsd,
  amountToStake,
  numberToStake,
  croPerStcro,
  apyList,
  oneYearUsdRewards,
  nSteps,
  currentStep,
}: StakeCroContentProps) => {
  const t = useTranslations();
  const coinModeInfo = COIN_MODE[coinMode];
  const { data: numberToStakeUsd } = useCoinUsdValue(numberToStake, coinMode);

  return (
    <>
      {currentView === StakeCroView.COMPLETE && (
        <StakeTransactionCompleted.Content
          coinMode={coinMode}
          receivedAmount={receivedLCoin as BigNumber}
        />
      )}
      {currentView === StakeCroView.PENDING && (
        <TransactionPending.Content nSteps={nSteps} currentStep={currentStep} />
      )}
      {currentView === StakeCroView.ERROR && (
        <TransactionError.Content error={error} />
      )}
      {currentView === StakeCroView.CONFIRM && (
        <Flex minH="full" flexDir="column" gap={4}>
          <Balance
            title={t('Amount to stake')}
            amount={numberToStake}
            icon={<coinModeInfo.Icon />}
            usdAmount={numberToStakeUsd}
            usdDisplayDecimals={0}
            showUsdAmount
          />
          <Info
            title={t('You will receive')}
            value={
              !amountToStake || isNaN(numberToStake) || numberToStake <= 0
                ? '0'
                : croPerStcro
                ? `${formatNumber(
                    Math.floor((100 * numberToStake) / croPerStcro) / 100,
                    2,
                  )} ${coinModeInfo.liquidName}`
                : undefined
            }
            mt="20px"
          />
          <Info
            title={t('Exchange rate')}
            value={
              croPerStcro
                ? `1 ${coinModeInfo.liquidName} = ${croPerStcro.toFixed(4)} ${
                    coinModeInfo.name
                  }`
                : undefined
            }
          />
          <Info
            className="veno-tour__stake-modal-rewards"
            title={t('Estimated rewards')}
            convertedValue={
              !isNaN(oneYearUsdRewards ?? 0)
                ? `${formatNumber(oneYearUsdRewards ?? 0, 2)} USD`
                : undefined
            }
            value={
              apyList.length < 3 || !Number(amountToStake) ? (
                <>
                  <Text as="span" whiteSpace="nowrap">
                    0.00 {coinModeInfo.name}
                  </Text>
                  <Text as="span" whiteSpace="nowrap">
                    {' '}
                    {t('in 1 year')}
                  </Text>
                </>
              ) : (
                <>
                  <Text as="span" whiteSpace="nowrap">
                    {(Number(amountToStake) * apyList[2]).toFixed(2)}{' '}
                    {coinModeInfo.name}
                  </Text>{' '}
                  <Text as="span" whiteSpace="nowrap">
                    {' '}
                    {t('in 1 year')}
                  </Text>
                </>
              )
            }
          />
          <MonthlyRewardChart
            coinMode={coinMode}
            my={{ desktop: 1 }}
            amount={numberToStake}
            smallChart
          />
        </Flex>
      )}
    </>
  );
};

type StakeCroFooterProps = {
  coinMode: CoinMode;
  currentView: StakeCroView;
  closeAndReset: () => void;
  transactionPending: boolean;
  doStake: () => Promise<void>;
};
const StakeCroFooter = ({
  coinMode,
  currentView,
  closeAndReset,
  transactionPending,
  doStake,
}: StakeCroFooterProps) => {
  const t = useTranslations();
  return (
    <>
      {currentView === StakeCroView.COMPLETE && (
        <StakeTransactionCompleted.Footer
          coinMode={coinMode}
          onCloseDrawer={closeAndReset}
        />
      )}
      {currentView === StakeCroView.PENDING && <TransactionPending.Footer />}
      {currentView === StakeCroView.CONFIRM && (
        <Button
          className="veno-tour__stake-modal-confirm-button"
          onClick={doStake}
          isLoading={transactionPending}
        >
          {t('Confirm Staking')}
        </Button>
      )}
    </>
  );
};

export default StakeCroDrawer;
